import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import type { ChartSlice } from '../../contracts'
import { formatMoney } from '../../formatters'
import { colorForLabel } from './colors'

interface AllocationDonutProps {
  title?: string
  slices: ChartSlice[]
  height?: number
}

export function AllocationDonut({ title, slices, height = 220 }: AllocationDonutProps) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0)
  const data = slices.map((slice, index) => ({
    label: slice.label,
    value: slice.value,
    color: colorForLabel(slice.label, index),
  }))

  if (slices.length === 0 || total <= 0) {
    return (
      <div className="chart-empty">
        <p className="muted">No allocation rows for this client.</p>
      </div>
    )
  }

  return (
    <div className="chart-block">
      {title ? <h4>{title}</h4> : null}
      <div className="chart-block__body">
        <div className="chart-block__canvas" style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius="55%"
                outerRadius="85%"
                paddingAngle={1}
                stroke="none"
              >
                {data.map((entry) => (
                  <Cell key={entry.label} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="chart-block__center" aria-hidden="true">
            <strong>{formatMoney(total)}</strong>
            <span>total</span>
          </div>
        </div>
        <ul className="chart-block__legend">
          {data.map((entry) => {
            const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0
            return (
              <li key={entry.label}>
                <span
                  className="chart-block__swatch"
                  style={{ background: entry.color }}
                  aria-hidden="true"
                />
                <span className="chart-block__legend-text">
                  <strong>{entry.label}</strong>
                  <small className="muted">
                    {formatMoney(entry.value)} · {pct}%
                  </small>
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
